import React from 'react';
import { Box } from '@mui/material';
import { useReducedMotion } from 'framer-motion';

interface MarqueeProps {
  items: string[];
  speed?: number; // seconds for one full loop
}

/**
 * A slow, seamless horizontal marquee. The track is duplicated so the loop is
 * gapless. Pauses on hover; static (wraps) under reduced-motion.
 */
const Marquee: React.FC<MarqueeProps> = ({ items, speed = 38 }) => {
  const prefersReduced = useReducedMotion();

  const Item = ({ label }: { label: string }) => (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        px: 3,
        fontFamily: (t) => t.typography.button.fontFamily,
        fontSize: { xs: '1.4rem', md: '2rem' },
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        '&::after': {
          content: '"✦"',
          color: 'primary.main',
          fontSize: '0.8em',
          opacity: 0.7,
        },
      }}
    >
      {label}
    </Box>
  );

  if (prefersReduced) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          py: 2,
          opacity: 0.8,
        }}
      >
        {items.map((label, i) => (
          <Item key={i} label={label} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: 2,
        // fade the edges so items appear/disappear gracefully
        maskImage:
          'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
        WebkitMaskImage:
          'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
        '&:hover .marquee-track': { animationPlayState: 'paused' },
      }}
    >
      <Box
        className="marquee-track"
        sx={{
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          willChange: 'transform',
          animation: `marquee-scroll ${speed}s linear infinite`,
          '@keyframes marquee-scroll': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-50%)' },
          },
        }}
      >
        {[...items, ...items].map((label, i) => (
          <Item key={i} label={label} />
        ))}
      </Box>
    </Box>
  );
};

export default Marquee;
